import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { cloneDeep, isEmpty } from 'lodash';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { bcSessionKeys } from 'common/common/constants/session-keys';
import { Attachments } from 'shared/components/attachments/attachments';
import { AttachmentError } from 'shared/components/email-attachments/attachment-error';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { WidgetBase } from 'common/panel-layout/widget-base';
import { BcRestApi } from 'common/api/rest-api';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { BcConfigService } from 'common/config/provider';
import { BcSettingsService } from 'common/settings/settings.service';
import { EmailsService } from 'common/emails/emails.service';
import { EmailAccountsService } from '../shared/services/email-accounts.service';
import { EmailSignatureInjectorService } from '../shared/services/email-signature-injector.service';
import { FileUploadService } from 'common/api/file-upload-service';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { TranslateService } from '@ngx-translate/core';
import { EditorMessages } from 'shared/components/editor-messages/editor-messages';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { workItemListEventRoutes } from 'common/workitems/lists/work-item-list-event-routes';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { CannedMessageRequest } from 'common/canned-messages/canned-message-request.interface';
import { broadcastEvents } from 'common/common/constants/broadcast-events';
import { CoolSessionStorage } from '@angular-cool/storage';
import { WorkItemsTrackingService } from 'common/workitems/workitems-tracking.service';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { WorkItemEventParams } from 'common/common/constants/analytics-enums';
import { NotificationBarService } from 'shared/services/notification/notification-bar.service';
import { CannedMessagePanelComponent } from 'shared/components/canned-message-panel/canned-message-panel.component';
import { AttachmentStorageService } from 'common/attachment-storage-service/attachment-storage.service';
import { InnerHtmlPipe } from 'shared/components/canned-message-panel/filters/inner-html.pipe';

@Component({
  selector: 'app-email-compose',
  templateUrl: './email-compose.component.html',
  styleUrls: ['./email-compose.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EmailComposeComponent extends WidgetBase implements OnInit, OnDestroy {
  @Input()
  public workItem;

  public editorMessages;
  public sendingInProgress;
  public isMoreActionsVisible;
  public cannedMessageRequest: CannedMessageRequest;
  public emailComposeStorageKey;
  public senderEmailAccount;
  public compose;
  public originalModel;
  public loading;
  public laneFocusedSubscription;
  public cannedMessageCloseSubscription;
  public cannedMessageSelectionSubscription;
  public attachments;
  public editorToolbarSettings;

  private static readonly InnerHtmlPipe = new InnerHtmlPipe();

  private _editorInstance;
  private _storedValues;
  private _trackSelectedItemSubscription: Subscription;

  constructor(private _bcRestApi: BcRestApi,
              private _bcAnalytics: BcAnalytics,
              private _bcNotificationService: NotificationBarService,
              private _bcConfig: BcConfigService,
              private _settingsService: BcSettingsService,
              private _xlcEmails: EmailsService,
              private _fileUploadService: FileUploadService,
              element: ElementRef,
              public renderer: Renderer2,
              private _translateService: TranslateService,
              workAreaManager: WorkAreaManager,
              private _attachmentStorageService: AttachmentStorageService,
              private _emailAccountsService: EmailAccountsService,
              private _emailSignatureInjectorService: EmailSignatureInjectorService,
              public _rxEmitter: RxEmitter,
              private _workItemStateHandler: WorkItemStateHandler,
              private _coolSessionStorage: CoolSessionStorage,
              private _workItemsTrackingService: WorkItemsTrackingService) {
    super(element.nativeElement, workAreaManager, renderer, _rxEmitter);

    this.editorMessages = new EditorMessages();
    this.sendingInProgress = null;
    this.isMoreActionsVisible = false;

    this.emailComposeStorageKey = bcSessionKeys.storage.emailCompose;

    this.compose = {
      from: {},
      to: '',
      emailToInvalid: true,
      cc: '',
      emailCcInvalid: false,
      bcc: '',
      emailBccInvalid: false,
      message: null
    };

    this._storedValues = [];

    this.originalModel = cloneDeep(this.compose);

    this.loading = false;

    this._emailAccountsService.getEmailAccounts().then((values) => {
      const draft = this._getDraftFromStorage();
      if (draft && !isEmpty(draft.from)) {
        this._storedValues = draft.from.items;
        this._setFromAddress(this._storedValues);
        if (values.length !== this._storedValues.length) {
          this._checkForNewAddresses(values, this._storedValues);
        }
      } else {
        this._setFromAddress(values);
      }
    });

    this.editorToolbarSettings = {
      toolbar: 'bold italic underline strikethrough link bullist numlist outdent indent undo redo insertImage'
    };
  }

  ngOnInit() {
    this.laneFocusedSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.SetDefaultFocusInLane}${this.workItem.id}`, () => {
      this._focusEditor();
    });

    this.cannedMessageCloseSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.CloseCannedMessagePanel}${this.workItem.id}`, () => {
      this.closeCannedMessagePanel();
    });

    this.cannedMessageSelectionSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.SendCannedMessageToEditor}${this.workItem.id}`, (args) => {
      if (args.eventSource === CannedMessagePanelComponent.name) {
        this.closeCannedMessagePanel();
      }
    });

    this.attachments = new Attachments(
      this._settingsService,
      this._bcAnalytics,
      this.editorMessages,
      this._fileUploadService,
      this._bcConfig,
      this._xlcEmails,
      this._attachmentStorageService.createOrGetStorage(this.workItem.id)
    );

    this.initTrackEvents();
  }

  ngOnDestroy() {
    this.laneFocusedSubscription.unsubscribe();
    this.cannedMessageCloseSubscription.unsubscribe();
    this.cannedMessageSelectionSubscription.unsubscribe();
    this._trackSelectedItemSubscription.unsubscribe();
    this._workItemsTrackingService.destroyItem(this.workItem.id);
    if (this.cannedMessageFolderDropdownSubscription) {
      this.cannedMessageFolderDropdownSubscription.unsubscribe();
      this.cannedMessageFolderDropdownSubscription = null;
    }
  }

  private initTrackEvents(): void {
    this._trackSelectedItemSubscription = this._workItemsTrackingService.onChange.pipe(filter(id => id === this.workItem.id)).subscribe(() => {
      this.trackSelectedEvent();
    });
  }

  cancelEmail() {
    this.compose = this.originalModel;
    this.resetComposeEmail();
  }

  toggleCannedMessagePanel() {
    if (this.isCannedMessagePanelOpen) {
      this.closeCannedMessagePanel();
    } else {
      this.openCannedMessagePanel();
    }
  }

  sendEmail() {
    if (this.isSendingInProgress()) {
      return this.sendingInProgress;
    }

    this.editorMessages.clearAllMessages();
    this.loading = true;

    const emailDetails = this.compose;

    if (isEmpty(this.attachments)) {
      this.sendingInProgress = this.processSendEmail(emailDetails);
    } else {
      const context = this;
      this.sendingInProgress = this.attachments.validateAttachmentsAvailability()
        .then(() => context.attachments.uploadAttachments())
        .then((response) => {
          const attachments = response && response.data ? response.data : null;
          return context.processSendEmail(emailDetails, attachments);
        })
        .catch((err) => {
          if (err instanceof AttachmentError) {
            context.editorMessages.removeEditorMessage('sending');
            context.editorMessages.addEditorMessage('invalidAttachment', 'bcEmailBox.invalidAttachment');
            context.loading = false;
          } else {
            context.editorMessages.removeEditorMessage('sending');
            context.editorMessages.addEditorMessage('sendingError', context.getTranslateCodeForStatusCodeType(err.status));
            context.sendingInProgress = null;
            context.loading = false;
            throw err;
          }
        });
    }

    return this.sendingInProgress;
  }

  isCannedMessagePanelVisible() {
    return this.isCannedMessagePanelOpen && this.senderEmailAccount;
  }

  isFocused(): boolean {
    return this._workItemStateHandler.isFocused(this.workItem.id);
  }

  isCMAndExpandedLayout() {
    return this.isCannedMessagePanelVisible() && this.isExpandedMode();
  }

  isSendingInProgress() {
    return !!this.sendingInProgress;
  }

  getTranslateCodeForStatusCodeType(statusCode) {
    let statusMessage;

    if (statusCode === 413) {
      statusMessage = 'bcEmailBox.oversizedAttachmentError';
    } else {
      statusMessage = 'bcEmailBox.sendingEmailError';
    }

    return statusMessage;
  }

  processSendEmail(emailDetails, attachments?) {
    this.editorMessages.addEditorMessage('sending', 'bcEmailCompose.sendingEmail');

    emailDetails.senderEmailAccountId = this.senderEmailAccount.value;

    return this._bcRestApi.sendNewEmailMessage(emailDetails, attachments)
      .then(() => {
        this.compose = this.originalModel;
        this.editorMessages.removeEditorMessage('sending');
        this.resetComposeEmail();
      })
      .catch(() => {
        this._bcNotificationService.addNotification({ id:'email-compose-error', content: 'bcEmailCompose.emailComposeError' });
        this.editorMessages.removeEditorMessage('sending');
        this.loading = false;
        this.sendingInProgress = false;
        this._emailAccountsService.reloadEmailAccounts().then(this._setFromAddress.bind(this));
      });
  }

  resetComposeEmail() {
    this.loading = false;
    this.sendingInProgress = false;
    this._attachmentStorageService.deleteStorage(this.workItem.id);
    this._removeEmailDraftFromStorage();

    this._rxEmitter.send(workItemListEventRoutes.composeEmailRemoved, this.workItem);
  }

  isSendEmailDenied() {
    return !this.senderEmailAccount || this.compose.emailToInvalid || this.loading;
  }

  _setFromAddress(accountList) {
    if (accountList) {
      if (accountList[0].fromEmail) {
        this.compose.from.items = accountList.map((account) => { return { label: account.fromEmail, value: account.id, selected: account.selected }; });
      } else {
        this.compose.from.items = accountList;
      }
    }
  }

  clearEditorMessage() {
    this.editorMessages.clearAllMessages();
    this.sendingInProgress = null;
  }

  onEditorSetupDone(editor) {
    this._editorInstance = editor;
    if (this.isFocused()) {
      this._focusEditor();
    }

    this._editorInstance.settings.placeholder = this._translateService.instant('bcEmailBox.form.messagePlaceholder');
  }

  toggleMoreActions() {
    this.isMoreActionsVisible = !this.isMoreActionsVisible;
  }

  _focusEditor() {
    if (!this._editorInstance) {
      return;
    }
    if (this._editorInstance.initialized) {
      this._rxEmitter.send(broadcastEvents.focusEditor, null);
    } else {
      this._editorInstance.on('init', () => {
        this._rxEmitter.send(broadcastEvents.focusEditor, null);
      });
    }
  }

  changeSenderEmailAccount(selectedItem) {
    if (!selectedItem) {
      return;
    }
    this.senderEmailAccount = selectedItem;
    this.cannedMessageRequest = {
      type: bcEnums.cannedMessageTypes.EMAIL,
      emailAccountId: selectedItem && selectedItem.value
    };

    if (this.senderEmailAccount) {
      this._emailSignatureInjectorService.addSignatureOfEmailAccount(this.senderEmailAccount.value).then((signature) => {
        const signatureElement = this._editorInstance.dom.select('div', 'email-signature');
        const signatureTag = document.createElement('div');
        signatureTag.className = 'email-signature';
        signatureTag.innerHTML = EmailComposeComponent.InnerHtmlPipe.transform(signature);

        if (signatureElement[0]) {
          this._editorInstance.dom.replace(signatureTag, signatureElement[0]);
        } else {
          this._editorInstance.dom.add(this._editorInstance.getBody(), signatureTag);
        }
      });
    }
  }

  // code smell: unimplemented feature could be deleted safely
  _sendCannedMessagePanelAnalytics(_action) {
    /* block */
  }

  _getDraftFromStorage() {
    const drafts = JSON.parse(this._coolSessionStorage.getItem(bcSessionKeys.storage.drafts));
    return drafts ? drafts['email-compose'] : null;
  }

  _removeEmailDraftFromStorage() {
    let draft = JSON.parse(this._coolSessionStorage.getItem(bcSessionKeys.storage.drafts));
    delete draft['email-compose'];
    this._coolSessionStorage.setItem('drafts', JSON.stringify(draft));
  }

  _checkForNewAddresses(newValues, originalValues) {
    let previouslySelected;
    for (let item of this._storedValues) {
      if (item.selected) {
        previouslySelected = item;
      }
    }
    if (!previouslySelected) {
      this._setFromAddress(newValues);
    } else if (newValues.length !== originalValues.length) {
      this._storedValues = newValues;
      for (let item of this._storedValues) {
        if (item.hasOwnProperty('selected')) {
          delete item.selected;
        }
        item.selected = item.fromEmail === previouslySelected.label;
      }
      this._setFromAddress(this._storedValues);
    }
  }

  private trackSelectedEvent() {
    this._workItemsTrackingService.track(this.workItem, {
      [WorkItemEventParams.CANNED_MESSAGES_PANEL_VISIBLE]: !!this.isCannedMessagePanelVisible()
    });
  }
}
